import { Fragment, useState } from 'react';

/**
 * A one-time-code field drawn as one rounded box per digit. It's a single
 * real <input> laid over the boxes, so phone keyboards, pasting a whole code
 * and iOS/Android one-time-code autofill all work as with any code field.
 * Eight-digit codes are shown as 4–4 for easier reading.
 */
export function CodeInput({
  id,
  length,
  value,
  onChange,
  onComplete,
  disabled,
  autoFocus,
}: {
  id: string;
  length: number;
  value: string;
  onChange: (value: string) => void;
  /** Called once all digits are entered, e.g. to submit the form. */
  onComplete?: (value: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
}) {
  const [focused, setFocused] = useState(false);
  const active = Math.min(value.length, length - 1);
  const split = length === 8 ? 4 : null;

  return (
    <div className="relative">
      <div className="flex items-center gap-1.5 sm:gap-2" aria-hidden>
        {Array.from({ length }, (_, i) => {
          const digit = value[i];
          const isActive = focused && i === active && value.length < length;
          return (
            <Fragment key={i}>
              {split === i && <span className="px-0.5 text-lg text-muted-foreground">–</span>}
              <div
                className={`flex h-12 min-w-0 flex-1 items-center justify-center rounded-lg border-2 text-xl font-semibold tabular-nums transition-colors sm:h-14 ${
                  isActive
                    ? 'border-primary ring-2 ring-primary/25'
                    : digit
                      ? 'border-primary/50 bg-primary/5'
                      : 'border-border'
                } ${disabled ? 'opacity-60' : ''}`}
              >
                {digit ??
                  (isActive ? <span className="h-6 w-0.5 animate-pulse bg-primary" /> : null)}
              </div>
            </Fragment>
          );
        })}
      </div>
      <input
        id={id}
        name="code"
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern={`[0-9]{${length}}`}
        maxLength={length}
        required
        disabled={disabled}
        autoFocus={autoFocus}
        aria-describedby={`${id}-hint`}
        value={value}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(e) => {
          const next = e.target.value.replace(/\D/g, '').slice(0, length);
          onChange(next);
          if (next.length === length && value.length < length) onComplete?.(next);
        }}
        // Invisible but on top of the boxes, so taps and focus land on it.
        // 16px text keeps iOS from zooming in when it's focused.
        className="absolute inset-0 h-full w-full cursor-text text-base opacity-0"
      />
      <p id={`${id}-hint`} className="sr-only">
        {length}-digit code
      </p>
    </div>
  );
}
